import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ImageUploader from '../../components/ImageUploader';
import { resolveImageUrl } from '../../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminGallery() {
  const { t, getLocalizedContent } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    title: { fr: '', en: '' },
    image_url: '',
    category: 'general',
    order: 0,
    is_active: true
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/gallery?active_only=false`);
      setItems(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/gallery`, formData);
      toast.success('Image ajoutée');
      setShowModal(false);
      setFormData({ title: { fr: '', en: '' }, image_url: '', category: 'general', order: 0, is_active: true });
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`);
      toast.success('Supprimé');
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{t('admin_gallery')}</h1>
        <Button onClick={() => setShowModal(true)} className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
          <Plus className="h-4 w-4 mr-2" />{t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div></div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {items.map((item) => (
            <div key={item.id} className="relative group aspect-square rounded-lg overflow-hidden">
              <img src={resolveImageUrl(item.image_url)} alt={getLocalizedContent(item.title)} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Button variant="destructive" size="sm" onClick={() => handleDelete(item.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-2">
                <p className="text-white text-xs truncate">{getLocalizedContent(item.title)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter une image</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Titre (FR)</Label><Input value={formData.title.fr} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })} /></div>
              <div><Label>Titre (EN)</Label><Input value={formData.title.en} onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })} /></div>
            </div>
            <div>
              <ImageUploader
                label="Image *"
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                testIdPrefix="gallery-image"
              />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Général</SelectItem>
                  <SelectItem value="chantiers">Chantiers</SelectItem>
                  <SelectItem value="equipe">Équipe</SelectItem>
                  <SelectItem value="bureaux">Bureaux</SelectItem>
                  <SelectItem value="evenements">Événements</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>{t('common_cancel')}</Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">{t('common_save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
